import { supabase } from '@/lib/supabase';
import { Expense, PaginatedResult } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchExpensesParams {
  search?: string;
  category?: string;
  vendor?: string;
  paymentMethod?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
}

export async function fetchExpenses(
  params: FetchExpensesParams
): Promise<PaginatedResult<Expense> & { totalAmount: number }> {
  const {
    search = '',
    category = '',
    vendor = '',
    paymentMethod = '',
    startDate = '',
    endDate = '',
    page = 0,
  } = params;

  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('expenses')
    .select('*', { count: 'exact' })
    .order('expense_date', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`description.ilike.%${s}%,vendor.ilike.%${s}%,notes.ilike.%${s}%`);
  }

  if (category) query = query.eq('category', category);
  if (vendor) query = query.ilike('vendor', `%${vendor.trim()}%`);
  if (paymentMethod) query = query.eq('payment_method', paymentMethod);
  if (startDate) query = query.gte('expense_date', startDate);
  if (endDate) query = query.lte('expense_date', endDate);

  const { data, error, count } = await query;
  if (error) throw error;

  const items = (data ?? []) as Expense[];
  const totalAmount = items.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);

  return { data: items, count: count ?? 0, totalAmount };
}

export async function createExpense(payload: {
  expense_date: string;
  category: string;
  description: string;
  vendor?: string | null;
  amount: number;
  payment_method?: string | null;
  receipt_path?: string | null;
  notes?: string | null;
}): Promise<Expense> {
  const { data, error } = await supabase
    .from('expenses')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data as Expense;
}

export async function updateExpense(
  id: string,
  payload: Partial<Omit<Expense, 'id' | 'created_at' | 'created_by'>>
): Promise<Expense> {
  const { data, error } = await supabase
    .from('expenses')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Expense;
}

export async function uploadExpenseReceipt(file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png';
  const path = `${Date.now()}_${Math.random().toString(36).substring(2)}.${ext}`;

  const { error } = await supabase.storage.from('expense-receipts').upload(path, file);
  if (error) throw error;
  return path;
}
